import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";

const STATUS_CLEAN = "clean";
const STATUS_DIRTY = "dirty";
const STATUS_SUBMITTING = "submitting";
const STATUS_ERROR = "error";

const EMPTY_META_SNAPSHOT = Object.freeze({
    pendingCount: 0,
    canUndo: false,
    canRedo: false,
});

const EMPTY_UNSUBSCRIBE = () => {};

let changeSetSequence = 0;

function normalizeCellKey(cellKey) {
    return typeof cellKey === "string" ? cellKey : null;
}

function getTimestamp() {
    return Date.now();
}

function createChangeSetId() {
    changeSetSequence += 1;
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `edit-buffer-${Date.now()}-${changeSetSequence}`;
}

function valuesEqual(left, right) {
    return Object.is(left, right);
}

function cloneEditEntry(entry) {
    if (!entry) return entry;
    return { ...entry };
}

function clonePendingEdits(pendingEdits) {
    return new Map(Array.from(pendingEdits.entries(), ([cellKey, entry]) => [cellKey, cloneEditEntry(entry)]));
}

function buildMetaSnapshot(pendingEdits, undoStack, redoStack) {
    return Object.freeze({
        pendingCount: pendingEdits.size,
        canUndo: undoStack.length > 0,
        canRedo: redoStack.length > 0,
    });
}

function areMetaSnapshotsEqual(left, right) {
    return left.pendingCount === right.pendingCount && left.canUndo === right.canUndo && left.canRedo === right.canRedo;
}

function normalizeCellKeyList(cellKeys) {
    if (typeof cellKeys === "string") return [cellKeys];
    if (!cellKeys || typeof cellKeys[Symbol.iterator] !== "function") return [];
    return Array.from(cellKeys).filter((cellKey) => normalizeCellKey(cellKey) !== null);
}

function createEditBufferStore() {
    let pendingEdits = new Map();
    let undoStack = [];
    let redoStack = [];
    let baselineValues = new Map();
    let metaSnapshot = EMPTY_META_SNAPSHOT;
    const metaListeners = new Set();
    const cellListeners = new Map();

    function notifyListeners(listeners) {
        Array.from(listeners).forEach((listener) => listener());
    }

    function notifyMetaIfChanged() {
        const nextSnapshot = buildMetaSnapshot(pendingEdits, undoStack, redoStack);
        if (areMetaSnapshotsEqual(metaSnapshot, nextSnapshot)) return;
        metaSnapshot = nextSnapshot;
        notifyListeners(metaListeners);
    }

    function notifyCellKeys(cellKeys) {
        cellKeys.forEach((cellKey) => {
            const listeners = cellListeners.get(cellKey);
            if (listeners?.size) notifyListeners(listeners);
        });
    }

    function emitChange(cellKeys) {
        notifyMetaIfChanged();
        notifyCellKeys(cellKeys);
    }

    function getBaselineValue(cellKey, fallbackValue) {
        if (baselineValues.has(cellKey)) return baselineValues.get(cellKey);
        baselineValues.set(cellKey, fallbackValue);
        return fallbackValue;
    }

    function stackReferencesCellKey(cellKey) {
        const stackReferencesCell = (stack) => stack.some((changeSet) => (
            changeSet?.changes || []
        ).some((change) => change?.cellKey === cellKey));
        return stackReferencesCell(undoStack) || stackReferencesCell(redoStack);
    }

    function reconcileBaselineValues() {
        Array.from(baselineValues.keys()).forEach((cellKey) => {
            if (!pendingEdits.has(cellKey) && !stackReferencesCellKey(cellKey)) {
                baselineValues.delete(cellKey);
            }
        });
    }

    function getActiveEntryPreviousValue(cellKey, fallbackPreviousValue) {
        for (let stackIndex = undoStack.length - 1; stackIndex >= 0; stackIndex -= 1) {
            const changes = undoStack[stackIndex]?.changes || [];
            for (let changeIndex = changes.length - 1; changeIndex >= 0; changeIndex -= 1) {
                const change = changes[changeIndex];
                if (change?.cellKey === cellKey) return change.before?.value;
            }
        }

        return fallbackPreviousValue;
    }

    function setPendingValue(cellKey, value, fallbackPreviousValue, entryPreviousValue, timestamp) {
        const baselineValue = getBaselineValue(cellKey, fallbackPreviousValue);
        const existing = pendingEdits.get(cellKey);

        if (valuesEqual(value, baselineValue)) {
            if (!existing) return false;
            pendingEdits.delete(cellKey);
            return true;
        }

        const nextEntry = {
            value,
            previousValue: entryPreviousValue,
            status: STATUS_DIRTY,
            timestamp,
        };

        if (
            existing
            && valuesEqual(existing.value, nextEntry.value)
            && valuesEqual(existing.previousValue, nextEntry.previousValue)
            && existing.status === nextEntry.status
            && existing.errorCode === undefined
            && existing.errorMessage === undefined
        ) {
            return false;
        }

        pendingEdits.set(cellKey, nextEntry);
        return true;
    }

    function applyChangeSetValues(changeSet, valueKey, { timestamp = getTimestamp(), getEntryPreviousValue } = {}) {
        const orderedChanges = valueKey === "before" ? [...(changeSet?.changes || [])].reverse() : changeSet?.changes || [];
        const changedCellKeys = new Set();

        orderedChanges.forEach((change) => {
            const cellKey = normalizeCellKey(change?.cellKey);
            if (cellKey === null) return;
            const nextValue = change?.[valueKey]?.value;
            const fallbackPreviousValue = change?.before?.value;
            const entryPreviousValue = getEntryPreviousValue
                ? getEntryPreviousValue(cellKey, fallbackPreviousValue)
                : fallbackPreviousValue;
            if (setPendingValue(cellKey, nextValue, fallbackPreviousValue, entryPreviousValue, timestamp)) {
                changedCellKeys.add(cellKey);
            }
        });

        return changedCellKeys;
    }

    function buildChanges(edits) {
        const workingValues = new Map();
        const changes = [];

        edits.forEach((edit) => {
            const cellKey = normalizeCellKey(edit?.cellKey);
            if (cellKey === null) return;

            const beforeValue = workingValues.has(cellKey)
                ? workingValues.get(cellKey)
                : edit.previousValue;

            if (valuesEqual(beforeValue, edit.value)) return;

            if (!baselineValues.has(cellKey)) {
                baselineValues.set(cellKey, edit.previousValue);
            }

            changes.push({
                cellKey,
                before: { value: beforeValue },
                after: { value: edit.value },
            });
            workingValues.set(cellKey, edit.value);
        });

        return changes;
    }

    function applyChangeSet(changeSet) {
        undoStack = [...undoStack, changeSet];
        const changedCellKeys = applyChangeSetValues(changeSet, "after", { timestamp: changeSet.timestamp });
        redoStack = [];
        emitChange(changedCellKeys);
        return changeSet;
    }

    function applyBatch(edits = []) {
        if (!Array.isArray(edits) || !edits.length) return null;

        const changes = buildChanges(edits);
        if (!changes.length) {
            reconcileBaselineValues();
            return null;
        }

        return applyChangeSet({
            id: createChangeSetId(),
            changes,
            timestamp: getTimestamp(),
        });
    }

    function applyEdit(cellKey, value, previousValue) {
        return applyBatch([{ cellKey, value, previousValue }]);
    }

    return {
        subscribeMeta(listener) {
            metaListeners.add(listener);
            return () => metaListeners.delete(listener);
        },
        subscribeCell(cellKey, listener) {
            const normalizedCellKey = normalizeCellKey(cellKey);
            if (normalizedCellKey === null) return EMPTY_UNSUBSCRIBE;

            const listeners = cellListeners.get(normalizedCellKey) || new Set();
            listeners.add(listener);
            cellListeners.set(normalizedCellKey, listeners);
            return () => {
                listeners.delete(listener);
                if (!listeners.size) cellListeners.delete(normalizedCellKey);
            };
        },
        getMetaSnapshot() {
            return metaSnapshot;
        },
        applyEdit,
        applyBatch,
        undo() {
            if (!undoStack.length) return null;

            const changeSet = undoStack[undoStack.length - 1];
            undoStack = undoStack.slice(0, -1);
            redoStack = [...redoStack, changeSet];
            const changedCellKeys = applyChangeSetValues(changeSet, "before", {
                getEntryPreviousValue: getActiveEntryPreviousValue,
            });
            reconcileBaselineValues();
            emitChange(changedCellKeys);
            return changeSet;
        },
        redo() {
            if (!redoStack.length) return null;

            const changeSet = redoStack[redoStack.length - 1];
            redoStack = redoStack.slice(0, -1);
            undoStack = [...undoStack, changeSet];
            const changedCellKeys = applyChangeSetValues(changeSet, "after");
            emitChange(changedCellKeys);
            return changeSet;
        },
        discardAll() {
            if (!pendingEdits.size && !undoStack.length && !redoStack.length && !baselineValues.size) return;

            const changedCellKeys = new Set(pendingEdits.keys());
            pendingEdits = new Map();
            undoStack = [];
            redoStack = [];
            baselineValues = new Map();
            emitChange(changedCellKeys);
        },
        getCellStatus(cellKey) {
            const normalizedCellKey = normalizeCellKey(cellKey);
            if (normalizedCellKey === null) return STATUS_CLEAN;
            return pendingEdits.get(normalizedCellKey)?.status || STATUS_CLEAN;
        },
        getCellValue(cellKey, serverValue) {
            const normalizedCellKey = normalizeCellKey(cellKey);
            if (normalizedCellKey === null) return serverValue;
            return pendingEdits.has(normalizedCellKey) ? pendingEdits.get(normalizedCellKey).value : serverValue;
        },
        getPendingEdits() {
            return clonePendingEdits(pendingEdits);
        },
        markSubmitting(cellKeys) {
            const timestamp = getTimestamp();
            const changedCellKeys = new Set();
            normalizeCellKeyList(cellKeys).forEach((cellKey) => {
                const entry = pendingEdits.get(cellKey);
                if (!entry || entry.status === STATUS_SUBMITTING) return;

                pendingEdits.set(cellKey, {
                    value: entry.value,
                    previousValue: entry.previousValue,
                    status: STATUS_SUBMITTING,
                    timestamp,
                });
                changedCellKeys.add(cellKey);
            });

            if (changedCellKeys.size) emitChange(changedCellKeys);
            return changedCellKeys.size;
        },
        applySubmitResults(results = []) {
            if (!Array.isArray(results) || !results.length) return { successCount: 0, errorCount: 0 };

            const timestamp = getTimestamp();
            const changedCellKeys = new Set();
            const successCellKeys = new Set();
            let errorCount = 0;

            results.forEach((result) => {
                const cellKey = normalizeCellKey(result?.cellKey);
                if (cellKey === null) return;

                if (result.success === true) {
                    if (!pendingEdits.has(cellKey)) return;
                    pendingEdits.delete(cellKey);
                    successCellKeys.add(cellKey);
                    changedCellKeys.add(cellKey);
                    return;
                }

                const entry = pendingEdits.get(cellKey);
                if (!entry) return;

                pendingEdits.set(cellKey, {
                    value: entry.value,
                    previousValue: entry.previousValue,
                    status: STATUS_ERROR,
                    errorCode: result.errorCode ?? result.error_code,
                    errorMessage: result.errorMessage ?? result.error_message,
                    timestamp,
                });
                errorCount += 1;
                changedCellKeys.add(cellKey);
            });

            if (successCellKeys.size) {
                undoStack = [];
                redoStack = [];
                successCellKeys.forEach((cellKey) => {
                    if (!pendingEdits.has(cellKey)) baselineValues.delete(cellKey);
                });
                reconcileBaselineValues();
            }

            if (changedCellKeys.size || successCellKeys.size) emitChange(changedCellKeys);
            return { successCount: successCellKeys.size, errorCount };
        },
        clearError(cellKey) {
            const normalizedCellKey = normalizeCellKey(cellKey);
            if (normalizedCellKey === null) return false;

            const entry = pendingEdits.get(normalizedCellKey);
            if (!entry || entry.status !== STATUS_ERROR) return false;

            pendingEdits.set(normalizedCellKey, {
                value: entry.value,
                previousValue: entry.previousValue,
                status: STATUS_DIRTY,
                timestamp: getTimestamp(),
            });
            emitChange(new Set([normalizedCellKey]));
            return true;
        },
    };
}

function resolveEditBufferStore(editBufferOrStore) {
    return editBufferOrStore?.store || editBufferOrStore || null;
}

export function useEditBuffer() {
    const storeRef = useRef(null);
    if (!storeRef.current) storeRef.current = createEditBufferStore();

    const store = storeRef.current;
    const metaSnapshot = useSyncExternalStore(
        store.subscribeMeta,
        store.getMetaSnapshot,
        store.getMetaSnapshot,
    );

    return useMemo(
        () => ({
            store,
            applyEdit: store.applyEdit,
            applyBatch: store.applyBatch,
            undo: store.undo,
            redo: store.redo,
            discardAll: store.discardAll,
            getCellStatus: store.getCellStatus,
            getCellValue: store.getCellValue,
            getPendingEdits: store.getPendingEdits,
            markSubmitting: store.markSubmitting,
            applySubmitResults: store.applySubmitResults,
            clearError: store.clearError,
            pendingCount: metaSnapshot.pendingCount,
            canUndo: metaSnapshot.canUndo,
            canRedo: metaSnapshot.canRedo,
        }),
        [metaSnapshot, store],
    );
}

export function useEditBufferCellStatus(editBufferOrStore, cellKey) {
    const store = resolveEditBufferStore(editBufferOrStore);
    const subscribe = useCallback(
        (listener) => store?.subscribeCell(cellKey, listener) ?? EMPTY_UNSUBSCRIBE,
        [cellKey, store],
    );
    const getSnapshot = useCallback(
        () => store?.getCellStatus(cellKey) ?? STATUS_CLEAN,
        [cellKey, store],
    );

    return useSyncExternalStore(subscribe, getSnapshot, () => STATUS_CLEAN);
}

export function useEditBufferCellValue(editBufferOrStore, cellKey, serverValue) {
    const store = resolveEditBufferStore(editBufferOrStore);
    const subscribe = useCallback(
        (listener) => store?.subscribeCell(cellKey, listener) ?? EMPTY_UNSUBSCRIBE,
        [cellKey, store],
    );
    const getSnapshot = useCallback(
        () => (store ? store.getCellValue(cellKey, serverValue) : serverValue),
        [cellKey, serverValue, store],
    );

    return useSyncExternalStore(subscribe, getSnapshot, () => serverValue);
}

export default useEditBuffer;
